import React, { useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr, downloadXlsx } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Package, Plus, Search, FileSpreadsheet, Upload, Loader2 } from "lucide-react";
import { CatalogRow } from "@/components/catalog/CatalogRow";
import { ItemDialog } from "@/components/catalog/ItemDialog";
import { useAuth } from "@/context/AuthContext";

export default function Catalog() {
  const { user } = useAuth();
  const canEdit = ["owner", "manager"].includes(user?.role);
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [offerFilter, setOfferFilter] = useState("all");
  const [edit, setEdit] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["catalog"], queryFn: async () => (await api.get("/recipes/ingredients")).data });
  const { data: vend } = useQuery({ queryKey: ["vendors-lite"], queryFn: async () => (await api.get("/procurement/vendors-lite")).data });
  const vendors = vend?.rows || [];
  const rows = data?.rows || [];
  const cats = useMemo(() => [...new Set(rows.map((r) => r.category).filter(Boolean))].sort((a, b) => a.localeCompare(b, "el")), [rows]);
  const filtered = rows.filter((r) => (cat === "all" || r.category === cat)
    && (!q || r.name.toLowerCase().includes(q.toLowerCase()))
    && (offerFilter === "all" || (offerFilter === "yes" ? r.needs_offer !== false : r.needs_offer === false)));
  const refresh = () => ["catalog", "ingredients", "bidding"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));

  const imp = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append("file", file);
      return (await api.post("/procurement/catalog/import", fd)).data;
    },
    onSuccess: (d) => { refresh(); toast.success(`Νέα: ${d.created} · Ενημερώθηκαν: ${d.updated} · Παραλείφθηκαν: ${d.skipped}`); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const btn = "inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] bg-white px-3.5 py-2 text-sm font-semibold text-[#45423C] hover:bg-[#F7F5F1] transition-colors";

  return (
    <div className="space-y-6" data-testid="catalog-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Προμήθειες</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#45423C] mt-1 flex items-center gap-3"><Package className="h-8 w-8" /> Κατάλογος Ειδών</h1>
          <p className="text-sm text-[#6F6A60] mt-2 max-w-2xl">Όλα τα είδη (κοινή λίστα με τα Υλικά των συνταγών). Σημειώστε από ποιους προμηθευτές θέλετε προσφορά — οι επιλογές περνούν στον Διαγωνισμό.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="catalog-export" className={btn} onClick={() => downloadXlsx("/procurement/catalog/export.xlsx", "TAF_Katalogos_Eidon.xlsx").catch((e) => toast.error(apiErr(e)))}><FileSpreadsheet className="h-4 w-4 text-[#3C7A3C]" /> Εξαγωγή Excel</button>
          {canEdit && <>
          <button data-testid="catalog-import" className={btn} disabled={imp.isPending} onClick={() => fileRef.current?.click()}>{imp.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Εισαγωγή Excel</button>
          <input ref={fileRef} type="file" accept=".xlsx" className="hidden" data-testid="catalog-import-file" onChange={(e) => { const f = e.target.files?.[0]; if (f) imp.mutate(f); e.target.value = ""; }} />
          <button data-testid="catalog-add" onClick={() => setEdit({})} className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2F2D29] transition-colors"><Plus className="h-4 w-4" /> Νέο είδος</button>
          </>}
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#B9AE9A]" />
          <input data-testid="catalog-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Αναζήτηση είδους…" className="w-full rounded-xl border border-[#E3DED4] pl-9 pr-3 py-2 text-sm" />
        </div>
        <select data-testid="catalog-category" value={cat} onChange={(e) => setCat(e.target.value)} className="rounded-xl border border-[#E3DED4] px-3 py-2 text-sm">
          <option value="all">Όλες οι κατηγορίες</option>
          {cats.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select data-testid="catalog-offer-filter" value={offerFilter} onChange={(e) => setOfferFilter(e.target.value)} className="rounded-xl border border-[#E3DED4] px-3 py-2 text-sm">
          <option value="all">Όλα</option><option value="yes">Θέλω προσφορά</option><option value="no">Χωρίς προσφορά</option>
        </select>
        <span className="text-sm text-[#6F6A60]" data-testid="catalog-count">{filtered.length} / {rows.length} είδη</span>
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F1EDE6] text-left text-[10px] uppercase tracking-wider text-[#6F6A60]">
              <th className="px-4 py-2.5">Είδος</th><th className="px-3 py-2.5">Κατηγορία</th><th className="px-3 py-2.5">Μον.</th>
              <th className="px-3 py-2.5 text-right">Τιμή</th><th className="px-3 py-2.5 text-center">Προσφορά;</th>
              <th className="px-3 py-2.5">Ζητάω προσφορά από</th><th className="px-3 py-2.5">Προτιμώμενος</th><th className="px-3 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 400).map((r) => <CatalogRow key={r.id} r={r} vendors={vendors} canEdit={canEdit} onEdit={() => setEdit(r)} onChanged={refresh} />)}
            {!isLoading && filtered.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-[#6F6A60]">Δεν βρέθηκαν είδη.</td></tr>}
            {isLoading && <tr><td colSpan={8} className="px-4 py-10 text-center text-[#6F6A60]">Φόρτωση…</td></tr>}
          </tbody>
        </table>
        {filtered.length > 400 && <p className="px-4 py-3 text-xs text-[#6F6A60]">Εμφανίζονται τα πρώτα 400 — περιορίστε με αναζήτηση ή κατηγορία.</p>}
      </div>

      <ItemDialog item={edit} cats={cats} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); refresh(); }} />
    </div>
  );
}
